// Display formatters and input adapters for metric/imperial.
// Stored values stay metric (m, m/s, km/h, m²); only display and input convert.

export type UnitSystem = 'metric' | 'imperial';

const FT = 0.3048; // m per ft, exact
const MI_FT = 5280;
const MPH = 0.44704; // m/s per mph, exact
const KM_PER_MI = 1.609344;
const ACRE_FT2 = 43560;
const SP = ' '; // thin space between value and unit

const fmt = (value: number, decimals: number, unit: string) => `${value.toFixed(decimals)}${SP}${unit}`;

/** The default before a user picks: metric everywhere, whatever the browser locale. */
export const DEFAULT_UNIT_SYSTEM: UnitSystem = 'metric';

export const lengthUnit = (u: UnitSystem): 'm' | 'ft' => (u === 'imperial' ? 'ft' : 'm');
export const speedUnit = (u: UnitSystem): 'm/s' | 'mph' => (u === 'imperial' ? 'mph' : 'm/s');
export const kmhUnit = (u: UnitSystem): 'km/h' | 'mph' => (u === 'imperial' ? 'mph' : 'km/h');

/** Metric: 1 decimal m, km (2 dp) above 1000 m. Imperial: whole ft, mi (2 dp) above 1000 ft. */
export function fmtLength(metres: number, u: UnitSystem): string {
  if (u === 'imperial') {
    const ft = metres / FT;
    return Math.abs(ft) > 1000 ? fmt(ft / MI_FT, 2, 'mi') : fmt(ft, 0, 'ft');
  }
  return Math.abs(metres) > 1000 ? fmt(metres / 1000, 2, 'km') : fmt(metres, 1, 'm');
}

/** Measured distances (the measure tool): 2 decimals in m or ft, never switching to km or mi. */
export function fmtDistance(metres: number, u: UnitSystem): string {
  return fmt(u === 'imperial' ? metres / FT : metres, 2, lengthUnit(u));
}

/** Metric: 2 decimals m/s. Imperial: 1 decimal mph. */
export function fmtSpeed(mps: number, u: UnitSystem): string {
  return u === 'imperial' ? fmt(mps / MPH, 1, 'mph') : fmt(mps, 2, 'm/s');
}

/** For stored km/h values. Metric: whole km/h. Imperial: whole mph. */
export function fmtSpeedKmh(kmh: number, u: UnitSystem): string {
  return u === 'imperial' ? fmt(kmh / KM_PER_MI, 0, 'mph') : fmt(kmh, 0, 'km/h');
}

/** Metric: 1 decimal m², ha (2 dp) above 10000 m². Imperial: whole ft², acres (2 dp) above 43560 ft². */
export function fmtArea(m2: number, u: UnitSystem): string {
  if (u === 'imperial') {
    const ft2 = m2 / (FT * FT);
    return Math.abs(ft2) > ACRE_FT2 ? fmt(ft2 / ACRE_FT2, 2, 'acres') : fmt(ft2, 0, 'ft²');
  }
  return Math.abs(m2) > 10000 ? fmt(m2 / 10000, 2, 'ha') : fmt(m2, 1, 'm²');
}

// Input adapters: display-unit value <-> stored metric value. No rounding, so round trips hold.
export const toMetres = (value: number, u: UnitSystem) => (u === 'imperial' ? value * FT : value);
export const fromMetres = (metres: number, u: UnitSystem) => (u === 'imperial' ? metres / FT : metres);
export const toMps = (value: number, u: UnitSystem) => (u === 'imperial' ? value * MPH : value);
export const fromMps = (mps: number, u: UnitSystem) => (u === 'imperial' ? mps / MPH : mps);
export const toKmh = (value: number, u: UnitSystem) => (u === 'imperial' ? value * KM_PER_MI : value);
export const fromKmh = (kmh: number, u: UnitSystem) => (u === 'imperial' ? kmh / KM_PER_MI : kmh);
